import React from 'react';
import { useI18n } from '../../context/I18nContext';
import { usageBarColors } from '../../styles/colors';
import { formatStorageUsageFromBytes } from '../../utils/storageQuota';

interface UsageBarProps {
  label: string;
  current: number;
  limit: number | null;
  unit?: string;
  /** Values are in bytes; display as Mo/Go (or MB/GB) */
  storageFromBytes?: boolean;
}

const UsageBar: React.FC<UsageBarProps> = ({
  label,
  current,
  limit,
  unit = '',
  storageFromBytes = false,
}) => {
  const { t, language } = useI18n();

  const percentage = limit === null ? 100 : Math.min((current / limit) * 100, 100);
  const isUnlimited = limit === null;
  const isHigh = !isUnlimited && current >= (limit * 0.9);
  const isMedium = !isUnlimited && current >= (limit * 0.7);

  // Pastel colors matching badge style: green (low/unlimited), amber (medium), red (high)
  const barColor = isUnlimited
    ? usageBarColors.unlimited
    : isHigh
    ? usageBarColors.high
    : isMedium
    ? usageBarColors.medium
    : usageBarColors.low;

  const displayText = storageFromBytes
    ? formatStorageUsageFromBytes(current, limit, language, t('settings.unlimited'))
    : (() => {
        const displayValue = limit === null ? t('settings.unlimited') : limit;
        return unit ? `${current} ${unit} / ${displayValue} ${unit}` : `${current} / ${displayValue}`;
      })();

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h5 className="font-medium text-gray-900">{label}</h5>
        <span className="text-sm text-gray-600">{displayText}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full ${barColor}`}
          style={{
            width: isUnlimited ? '100%' : `${percentage}%`,
          }}
        />
      </div>
    </div>
  );
};

export default UsageBar;
